import {
  Button,
  ButtonSize,
  ButtonVariant,
  Hide,
  Show
} from "shared/components";
import { SaveCancelButtons } from "shared/app/SaveCancelButtons";
import { Row } from "views/applications-id-report/components/Row";
import { useUnitsPropertiesDictionary } from "shared/hooks/use-units-properties";
import { useState } from "react";
import { Unit } from "types/landlord/address";
import { SectionCard } from "shared/app/SectionCard";
import { useFormTyped } from "shared/hooks";
import { toast } from "react-toastify";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import addressApi from "api/landlord/address";
import { getErrorMessage } from "utils/error";
import { UnitGeneralForm } from "shared/app/UnitGeneralForm";
import * as yup from "yup";
import { PropertyKind } from "shared/types";

const generalValidationSchema = yup.object().shape({
  title: yup.string().required("Unit name is required")
});

interface EditGeneralFormProps {
  onClose: () => void;
  unitId: number;
  propertyKind: PropertyKind | null;
  unitPmsId?: string;
  initialValues: {
    externalId: string;
    title: string;
    description: string;
  };
}

const EditGeneralForm = ({
  onClose,
  unitId,
  unitPmsId,
  propertyKind,
  initialValues
}: EditGeneralFormProps) => {
  const queryClient = useQueryClient();
  const { register, onSubmit } = useFormTyped(
    initialValues,
    generalValidationSchema
  );

  const { mutate: updateUnit, isPending } = useMutation({
    mutationFn: (values: EditGeneralFormProps["initialValues"]) => {
      return addressApi.editUnit(unitId, {
        external_id: values.externalId,
        title: values.title,
        description: values.description
      });
    },
    onSuccess: () => {
      toast.success("Unit updated successfully");
      queryClient.invalidateQueries({
        queryKey: ["unit"]
      });
      onClose();
    },
    onError: (error) => {
      toast.error(`Failed to update unit: ${getErrorMessage(error)}`);
    }
  });

  const handleSubmit = onSubmit({
    onSuccess: (values) => {
      updateUnit(values);
    },
    onError: () => {
      toast.error("Form validation failed");
    }
  });

  return (
    <UnitGeneralForm
      register={register}
      propertyKind={propertyKind}
      pmsId={unitPmsId}
      footer={
        <SaveCancelButtons
          onSave={(e) => handleSubmit(e)}
          onCancel={onClose}
          saveLoading={isPending}
        />
      }
    />
  );
};

export const GeneralSection = ({ unit }: { unit: Unit }) => {
  const { Units: UnitsLabel } = useUnitsPropertiesDictionary();
  const [isEditing, setIsEditing] = useState(false);

  if (isEditing)
    return (
      <EditGeneralForm
        onClose={() => setIsEditing(false)}
        unitId={unit.id}
        propertyKind={unit.property.kind}
        unitPmsId={unit.pms_id}
        initialValues={{
          externalId: unit.external_id || "",
          title: unit.title || "",
          description: unit.description || ""
        }}
      />
    );

  return (
    <SectionCard
      title="General"
      headerExtra={
        <Hide if={Boolean(unit.pms_id)}>
          <Button
            size={ButtonSize.SM}
            variant={ButtonVariant.BASE}
            onClick={() => setIsEditing(true)}
          >
            Edit
          </Button>
        </Hide>
      }
    >
      <div className="space-y-4">
        <Row title={`${UnitsLabel} name`} value={unit.title} />
        <Row title={`${UnitsLabel} ID`} value={unit.external_id} />
        <Row title="Street address" value={unit.property.address.address1} />
        <Row
          title="Apartment, suite, etc"
          value={unit.property.address.address2}
        />
        <Row title="State" value={unit.property.address.state_or_province} />
        <Row title="City" value={unit.property.address.city} />
        <Row title="Zip" value={unit.property.address.postal_code} />
        <Show if={unit.property.kind}>
          <Row
            title="Description"
            value={unit.description}
            valueClassName="w-[500px]"
          />
        </Show>
      </div>
    </SectionCard>
  );
};
